import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Input, message, Spin, Divider, Alert, Modal } from 'antd';
import { CheckIcon } from '@heroicons/react/24/outline';
import { planAPI, orderAPI, userAPI } from '../services/api';
import { PlanDetail, PaymentMethod, OrderDetail, Order } from '../types/order';
import type { Coupon } from '../types/order';
import { PeriodType, periodLabels } from '../types';
import { formatBytes } from '../utils/format';
import { showErrorToast, showToast } from '../utils/toast';
import { PlanContent, PlanContentMobile } from '../components/Plancontent';
import { useTelegram } from '../contexts/TelegramContext';

interface OrderDetailPageProps {
  planId?: number;
}

export const OrderDetailPage: React.FC<OrderDetailPageProps> = () => {
  const { planId: paramPlanId } = useParams<{ planId: string }>();
  const navigate = useNavigate();
  const { openLink, isInTelegram } = useTelegram();

  const [planDetail, setPlanDetail] = useState<PlanDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>('month_price');
  const [couponCode, setCouponCode] = useState('');
  const [couponResult, setCouponResult] = useState<{
    valid: boolean;
    coupon?: Coupon;
    discount_amount?: number;
    message?: string;
  } | null>(null);
  const [couponChecking, setCouponChecking] = useState(false);
  const [orderCreating, setOrderCreating] = useState(false);
  const [couponLoading, setCouponLoading] = useState(false);
  const [orderLoading, setOrderLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedPayment, setSelectedPayment] = useState<number | null>(null);
  const [orderDetail, setOrderDetail] = useState<OrderDetail | null>(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showSubscriptionWarning, setShowSubscriptionWarning] = useState(false);
  // 二维码支付（桌面端展示，移动端跳转）
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  // 未支付订单相关逻辑由后端处理，前端不再保留相关状态
  // const [showUnpaidOrderWarning, setShowUnpaidOrderWarning] = useState(false);
  // const [userSubscription, setUserSubscription] = useState<any>(null);
  // const [unpaidOrders, setUnpaidOrders] = useState<Order[]>([]);
  // const [cancellingOrderId, setCancellingOrderId] = useState<number | null>(null);

  const planId = paramPlanId ? parseInt(paramPlanId) : 0;

  useEffect(() => {
    if (planId) {
      fetchPlanDetail();
      fetchPaymentMethods();
    }
  }, [planId]);

  useEffect(() => {
    // 当计划详情加载完成后，设置默认的订阅周期
    if (planDetail) {
      const availablePeriods = getAvailablePeriods(planDetail);
      if (availablePeriods.length > 0) {
        setSelectedPeriod(availablePeriods[0]);
      }
    }
  }, [planDetail]);

  useEffect(() => {
    // 当订阅周期改变时，清除优惠券结果
    setCouponResult(null);
    setCouponCode('');
  }, [selectedPeriod]);

  const fetchPlanDetail = async () => {
    try {
      setLoading(true);
      const response = await planAPI.getPlanDetail(planId);
      setPlanDetail(response.data.data);
    } catch (error: any) {
      showErrorToast(error.response?.data?.message || '获取计划详情失败');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await orderAPI.getPaymentMethods();
      const methods = response.data.data || [];
      setPaymentMethods(methods);
      if (methods.length > 0) {
        setSelectedPayment(methods[0].id);
      }
    } catch (error: any) {
      console.error('获取支付方式失败:', error);
    }
  };

  const handleCouponCheck = async () => {
    if (!couponCode.trim()) {
      setCouponResult(null);
      return;
    }

    try {
      setCouponLoading(true);
      const response = await planAPI.couponCheck(couponCode.trim(), planId);
      const result = response.data.data;

      // 状态码200表示优惠码有效
      const coupon = result.coupon || result;
      const originalPrice = planDetail ? getPlanPrice(planDetail, selectedPeriod) : 0;

      // 根据优惠券类型计算抵扣金额
      let discountAmount = 0;
      if (coupon.type === 1) {
        // 金额券：直接使用value作为抵扣金额
        discountAmount = coupon.value;
      } else if (coupon.type === 2) {
        // 比例券：按百分比计算抵扣金额
        discountAmount = Math.floor(originalPrice * coupon.value / 100);
      }

      setCouponResult({
        valid: true,
        coupon: coupon,
        discount_amount: discountAmount
      });
      showToast('优惠券验证成功');
    } catch (error: any) {
      setCouponResult({
        valid: false,
        message: error.response?.data?.message || '优惠券验证失败'
      });
      showErrorToast(error.response?.data?.message || '优惠券验证失败');
    } finally {
      setCouponLoading(false);
    }
  };

  const getPlanPrice = (plan: PlanDetail, period: PeriodType): number => {
    switch (period) {
      case 'month_price': return plan.month_price || 0;
      case 'quarter_price': return plan.quarter_price || 0;
      case 'half_year_price': return plan.half_year_price || 0;
      case 'year_price': return plan.year_price || 0;
      case 'two_year_price': return plan.two_year_price || 0;
      case 'three_year_price': return plan.three_year_price || 0;
      case 'onetime_price': return plan.onetime_price || 0;
      case 'reset_price': return plan.reset_price || 0;
      default: return 0;
    }
  };

  const getAvailablePeriods = (plan: PlanDetail): PeriodType[] => {
    const periods: PeriodType[] = [];
    if (plan.month_price) periods.push('month_price');
    if (plan.quarter_price) periods.push('quarter_price');
    if (plan.half_year_price) periods.push('half_year_price');
    if (plan.year_price) periods.push('year_price');
    if (plan.two_year_price) periods.push('two_year_price');
    if (plan.three_year_price) periods.push('three_year_price');
    if (plan.onetime_price) periods.push('onetime_price');
    if (plan.reset_price) periods.push('reset_price');
    return periods;
  };

  const calculateFinalPrice = () => {
    if (!planDetail) return 0;

    const originalPrice = getPlanPrice(planDetail, selectedPeriod);
    if (!couponResult?.valid || !couponResult.discount_amount) {
      return originalPrice;
    }

    // 直接使用已经计算好的抵扣金额
    return Math.max(0, originalPrice - couponResult.discount_amount);
  };

  const checkUserStatus = async () => {
    // 后端已处理未支付订单逻辑，前端不再进行检查或弹出提示，直接允许创建订单
    return true;
  };

  const handleCreateOrder = async () => {
    if (!planDetail) return;

    // 先检查用户状态
    const canProceed = await checkUserStatus();
    if (!canProceed) {
      return;
    }

    await createOrderDirect();
  };

  const createOrderDirect = async () => {
    if (!planDetail) return;

    try {
      setOrderLoading(true);

      const response = await orderAPI.saveOrder(planId, selectedPeriod, couponCode.trim());
      const orderData = response.data.data;

      // 获取订单详情
      const orderDetailResponse = await orderAPI.getOrderDetail(orderData);
      setOrderDetail(orderDetailResponse.data.data);
      setShowPaymentModal(true);

      showToast('订单创建成功');
    } catch (error: any) {
      showErrorToast(error.response?.data?.message || '创建订单失败');
    } finally {
      setOrderLoading(false);
    }
  };



  const handlePayment = async () => {
    if (!orderDetail || !selectedPayment) {
      showErrorToast('请选择支付方式');
      return;
    }

    try {
      setPaymentProcessing(true);
      const response = await orderAPI.checkOut(orderDetail.trade_no, selectedPayment);
      const paymentData = response.data;

      if (paymentData.type === -1) {
        showToast('支付成功！订单已完成，正在跳转...');
        setTimeout(() => {
          navigate(`/order/status/${orderDetail.trade_no}`);
        }, 1500);
      } else if (paymentData.type === 0) {
        // 二维码支付：桌面端展示二维码，移动端直接跳转
        const payLink: string = paymentData.data;
        const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

        if (isMobileDevice) {
          showToast('正在跳转到支付页面，请完成支付后返回查看订单状态');
          if (isInTelegram) {
            openLink(payLink);
          } else {
            window.location.href = payLink;
          }
          // 可选：跳转到订单状态页面（保留用户返回自由）
          // setTimeout(() => {
          //   navigate(`/order/status/${orderDetail.trade_no}?from=payment`);
          // }, 3000);
        } else {
          // 桌面端：生成二维码并展示模态框
          const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(payLink)}`;
          setQrCodeUrl(qrUrl);
          setShowQrModal(true);
          // 关闭支付方式选择模态框，避免叠加
          setShowPaymentModal(false);
          showToast('请使用手机扫码完成支付');
        }
      } else if (paymentData.type === 1) {
        // 跳转到支付页面
        showToast('正在跳转到支付页面，请完成支付后返回查看订单状态');
        if (isInTelegram) {
          openLink(paymentData.data);
        } else {
          window.location.href = paymentData.data;
        }
        // 延迟跳转到订单状态页面
        // setTimeout(() => {
        //   navigate(`/order/status/${orderDetail.trade_no}?from=payment`);
        // }, 3000);
      }
    } catch (error: any) {
      showErrorToast(error.response?.data?.message || '支付失败');
    } finally {
      setPaymentProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  if (!planDetail) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">计划不存在</p>
        <Button onClick={() => navigate('/')} className="mt-4">
          返回主页
        </Button>
      </div>
    );
  }

  const originalPrice = getPlanPrice(planDetail, selectedPeriod);
  const finalPrice = calculateFinalPrice();
  const discountAmount = originalPrice - finalPrice;
  const availablePeriods = planDetail ? getAvailablePeriods(planDetail) : [];

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="mb-4 sm:mb-6">
        <Button onClick={() => navigate('/')} className="mb-3 sm:mb-4 text-sm sm:text-base">
          ← 返回主页
        </Button>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">订阅详情</h1>
      </div>

      <div className="flex flex-col lg:grid lg:grid-cols-3 gap-4 lg:gap-6">
        {/* 计划信息 */}
        <div className="lg:col-span-2 order-1 lg:order-none">
          <Card title="计划信息" className="mb-4 lg:mb-6 shadow-sm">
            <div className="space-y-4">
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">{planDetail.name}</h3>
                <p className="text-xs sm:text-sm text-gray-600">计费周期: {periodLabels[selectedPeriod]}</p>
              </div>

              {availablePeriods.length > 1 && (
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    选择计费周期
                  </label>
                  <select
                    value={selectedPeriod}
                    onChange={(e) => setSelectedPeriod(e.target.value as PeriodType)}
                    className="block w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm sm:text-base"
                  >
                    {availablePeriods.map((period) => (
                      <option key={period} value={period}>
                        {periodLabels[period]} - ¥{(getPlanPrice(planDetail, period) / 100).toFixed(0)}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="flex items-center">
                  <CheckIcon className="h-5 w-5 text-green-500 mr-2" />
                  <span className="text-xs sm:text-sm">流量: {planDetail.transfer_enable}GB</span>
                </div>
                {planDetail.speed_limit && (
                  <div className="flex items-center">
                    <CheckIcon className="h-5 w-5 text-green-500 mr-2" />
                    <span className="text-xs sm:text-sm">限速: {planDetail.speed_limit} Mbps</span>
                  </div>
                )}
                {planDetail.capacity_limit && (
                  <div className="flex items-center">
                    <CheckIcon className="h-5 w-5 text-green-500 mr-2" />
                    <span className="text-xs sm:text-sm">设备限制: {planDetail.capacity_limit} 台</span>
                  </div>
                )}
              </div>

              {planDetail.content && (
                <div className="mt-4">
                  <div className="block sm:hidden">
                    <PlanContentMobile content={planDetail.content} className="text-xs" />
                  </div>
                  <div className="hidden sm:block">
                    <PlanContent content={planDetail.content} className="text-sm" />
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* 优惠券 */}
          <Card title="优惠券" className="mb-4 lg:mb-6 shadow-sm">
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  placeholder="请输入优惠券代码"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  onPressEnter={handleCouponCheck}
                  className="flex-1 text-sm sm:text-base"
                  size="large"
                />
                <Button
                  onClick={handleCouponCheck}
                  loading={couponLoading}
                  type="primary"
                  size="large"
                  className="w-full sm:w-auto min-w-[80px]"
                >
                  验证
                </Button>
              </div>

              {couponResult && (
                <Alert
                  type={couponResult.valid ? 'success' : 'error'}
                  message={couponResult.valid ? '优惠券有效' : '优惠券无效'}
                  description={couponResult.message || (couponResult.valid ? `优惠金额: ¥${((couponResult.discount_amount || 0) / 100).toFixed(2)}` : '')}
                  showIcon
                  className="text-xs sm:text-sm"
                />
              )}
            </div>
          </Card>
        </div>

        {/* 价格信息 */}
        <div className="order-2 lg:order-none">
          <Card title="价格详情" className="lg:sticky lg:top-4 shadow-sm mb-4 sm:mb-0">
            <div className="space-y-3">
              {/* 原价 */}
              <div className="flex justify-between text-sm sm:text-base">
                <span>套餐原价:</span>
                <span>¥{(originalPrice / 100).toFixed(2)}</span>
              </div>

              {/* 优惠券抵扣 */}
              {couponResult?.valid && couponResult.discount_amount && couponResult.discount_amount > 0 && (
                <div className="flex justify-between text-green-600 text-sm sm:text-base">
                  <span className="flex items-center">
                    <span>优惠券抵扣</span>
                    <span className="ml-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                      {couponResult.coupon?.type === 1 ? '金额券' : '折扣券'}
                    </span>
                  </span>
                  <span>-¥{(couponResult.discount_amount / 100).toFixed(2)}</span>
                </div>
              )}

              {/* 余额抵扣提示 */}
              <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-orange-400 rounded-full mr-2"></div>
                    <span className="text-sm font-medium text-orange-800">余额抵扣</span>
                    <span className="ml-2 text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
                      优先使用
                    </span>
                  </div>
                  <span className="text-sm text-orange-600">支付时自动抵扣</span>
                </div>
                <div className="mt-2 text-xs text-orange-600">
                  💰 使用余额支付可享受额外优惠，让您的每一分钱都更值钱！
                </div>
              </div>

              {/* 剩余价值抵扣提示 */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-blue-400 rounded-full mr-2"></div>
                    <span className="text-sm font-medium text-blue-800">剩余价值抵扣</span>
                    <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                      智能计算
                    </span>
                  </div>
                  <span className="text-sm text-blue-600">升级时自动计算</span>
                </div>
                <div className="mt-2 text-xs text-blue-600">
                  🔄 升级套餐时，系统会自动计算您当前套餐的剩余价值进行抵扣
                </div>
              </div>

              <Divider className="my-4" />

              {/* 应付金额 */}
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex justify-between items-center">
                  <span className="text-base sm:text-lg font-semibold text-gray-900">应付金额:</span>
                  <div className="text-right">
                    <div className="text-lg sm:text-xl font-bold text-primary-600">
                      ¥{(finalPrice / 100).toFixed(2)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      实际支付金额将根据余额自动调整
                    </div>
                  </div>
                </div>
              </div>

              <Button
                type="primary"
                size="large"
                block
                onClick={handleCreateOrder}
                loading={orderLoading}
                className="mt-4 h-12 text-base font-medium"
              >
                {orderLoading ? '创建订单中...' : '立即下单'}
              </Button>
            </div>
          </Card>
        </div>
      </div>

      {/* 支付模态框 */}
      <Modal
        title="选择支付方式"
        open={showPaymentModal}
        onCancel={() => setShowPaymentModal(false)}
        footer={null}
        width={600}
        className="mobile-modal"
        centered
      >
        {orderDetail && (
          <div className="space-y-4">
            {/* 订单基本信息 */}
            <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-2 space-y-1 sm:space-y-0">
                <span className="text-xs sm:text-sm text-gray-600">订单号:</span>
                <span className="font-mono text-xs sm:text-sm break-all">{orderDetail.trade_no}</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-1 sm:space-y-0">
                <span className="text-xs sm:text-sm text-gray-600">套餐名称:</span>
                <span className="text-xs sm:text-sm font-medium">{planDetail?.name}</span>
              </div>
            </div>

            {/* 费用明细 */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
              <h4 className="text-sm font-semibold text-blue-900 mb-3 flex items-center">
                <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                费用明细
              </h4>
              <div className="space-y-2">
                {/* 套餐原价 */}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">套餐原价:</span>
                  <span>¥{(originalPrice / 100).toFixed(2)}</span>
                </div>
                
                {/* 优惠券抵扣 */}
                {couponResult?.valid && couponResult.discount_amount && couponResult.discount_amount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span className="flex items-center">
                      优惠券抵扣
                      <span className="ml-1 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                        {couponResult.coupon?.type === 1 ? '金额券' : '折扣券'}
                      </span>
                    </span>
                    <span>-¥{(couponResult.discount_amount / 100).toFixed(2)}</span>
                  </div>
                )}
                
                {/* 余额抵扣说明 */}
                {orderDetail.balance_amount && orderDetail.balance_amount > 0 && (
                  <div className="flex justify-between text-sm text-orange-600">
                    <span className="flex items-center">
                      余额抵扣
                      <span className="ml-1 text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">
                        已抵扣
                      </span>
                    </span>
                    <span>-¥{(orderDetail.balance_amount / 100).toFixed(2)}</span>
                  </div>
                )}
                
                {/* 剩余价值抵扣 */}
                {orderDetail.surplus_amount && orderDetail.surplus_amount > 0 && (
                  <div className="flex justify-between text-sm text-blue-600">
                    <span className="flex items-center">
                      剩余价值抵扣
                      <span className="ml-1 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                        已抵扣
                      </span>
                    </span>
                    <span>-¥{(orderDetail.surplus_amount / 100).toFixed(2)}</span>
                  </div>
                )}
                
                <div className="border-t border-blue-200 pt-2 mt-3">
                  <div className="flex justify-between text-base font-semibold">
                    <span className="text-gray-900">实际支付:</span>
                    <span className="text-primary-600">¥{(orderDetail.total_amount / 100).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* 优惠提示 */}
            {(orderDetail.balance_amount && orderDetail.balance_amount > 0) || (orderDetail.surplus_amount && orderDetail.surplus_amount > 0) ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                  <span className="text-sm font-medium text-green-800">恭喜您享受了优惠！</span>
                </div>
                <div className="mt-1 text-xs text-green-600">
                  {orderDetail.balance_amount && orderDetail.balance_amount > 0 && `余额抵扣 ¥${(orderDetail.balance_amount / 100).toFixed(2)}`}
                  {orderDetail.balance_amount && orderDetail.balance_amount > 0 && orderDetail.surplus_amount && orderDetail.surplus_amount > 0 && ' + '}
                  {orderDetail.surplus_amount && orderDetail.surplus_amount > 0 && `剩余价值抵扣 ¥${(orderDetail.surplus_amount / 100).toFixed(2)}`}
                  ，为您节省了不少费用！
                </div>
              </div>
            ) : (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-orange-500 rounded-full mr-2"></div>
                  <span className="text-sm font-medium text-orange-800">支付提示</span>
                </div>
                <div className="mt-1 text-xs text-orange-600">
                  支付时将优先使用您的账户余额，让每一分钱都更值钱！
                </div>
              </div>
            )}

            <div>
              <h4 className="text-sm sm:text-base font-medium text-gray-900 mb-3">选择支付方式:</h4>
              <div className="space-y-2 sm:space-y-3">
                {paymentMethods.map((method) => (
                  <div
                    key={method.id}
                    className={`border rounded-lg p-3 sm:p-4 cursor-pointer transition-colors ${selectedPayment === method.id
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                      }`}
                    onClick={() => setSelectedPayment(method.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div
                          className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full border-2 mr-3 ${selectedPayment === method.id
                            ? 'border-primary-500 bg-primary-500'
                            : 'border-gray-300'
                            }`}
                        >
                          {selectedPayment === method.id && (
                            <div className="w-2 h-2 sm:w-3 sm:h-3 bg-white rounded-full mx-auto mt-0.5"></div>
                          )}
                        </div>
                        <span className="font-medium text-sm sm:text-base">{method.name}</span>
                      </div>
                      {(method.handling_fee_fixed || method.handling_fee_percent) && (
                        <span className="text-xs text-gray-500">
                          手续费: {method.handling_fee_fixed ? `¥${(method.handling_fee_fixed / 100).toFixed(2)}` : ''}
                          {method.handling_fee_percent ? `${method.handling_fee_percent}%` : ''}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              {/* <Button
                onClick={() => setShowPaymentModal(false)}
                className="w-full sm:flex-1 h-10 sm:h-auto"
                size="large"
              >
                取消
              </Button> */}
              <Button
                type="primary"
                onClick={handlePayment}
                loading={paymentProcessing}
                disabled={!selectedPayment}
                className="w-full sm:flex-1 h-10 sm:h-auto"
                size="large"
              >
                确认支付
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* 扫码支付模态框（桌面端） */}
      <Modal
        title="扫码支付"
        open={showQrModal}
        onCancel={() => setShowQrModal(false)}
        footer={null}
        width={420}
        className="mobile-modal"
        centered
      >
        <div className="flex flex-col items-center space-y-4">
          {qrCodeUrl && (
            <img
              src={qrCodeUrl}
              alt="支付二维码"
              className="w-60 h-60 rounded-md border"
            />
          )}
          <Alert
            type="info"
            message="请使用手机扫码完成支付"
            description="支付完成后，点击下方按钮查看订单状态。"
            showIcon
          />
          <div className="flex gap-3 w-full">
            <Button
              type="primary"
              block
              onClick={() => {
                setShowQrModal(false);
                if (orderDetail) {
                  navigate(`/order/status/${orderDetail.trade_no}?from=payment`);
                }
              }}
            >
              已完成支付，查看订单状态
            </Button>
            <Button
              block
              onClick={() => setShowQrModal(false)}
            >
              稍后再说
            </Button>
          </div>
        </div>
      </Modal>

      {/* 订阅覆盖警告模态框 */}
      {/* <Modal
        title="订阅覆盖提醒"
        open={showSubscriptionWarning}
        onCancel={() => setShowSubscriptionWarning(false)}
        footer={[
          <Button key="cancel" onClick={() => setShowSubscriptionWarning(false)}>
            取消
          </Button>,
          <Button
            key="confirm"
            type="primary"
            onClick={() => {
              setShowSubscriptionWarning(false);
              createOrderDirect();
            }}
            danger
          >
            确认覆盖
          </Button>,
        ]}
        width={500}
        className="mobile-modal"
        centered
      >
        <div className="space-y-4">
          <Alert
            type="warning"
            message="您当前已有有效订阅"
            description="新订单将会覆盖您当前的订阅计划。"
            showIcon
          />
          {userSubscription && (
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <div className="text-sm">
                <span className="text-gray-600">当前订阅：</span>
                <span className="font-medium">{userSubscription.plan?.name || '未知计划'}</span>
              </div>
              <div className="text-sm">
                <span className="text-gray-600">到期时间：</span>
                <span>{userSubscription.expired_at ? new Date(userSubscription.expired_at * 1000).toLocaleString() : '长期有效'}</span>
              </div>
              {userSubscription.transfer_enable && (
                <div className="text-sm">
                  <span className="text-gray-600">剩余流量：</span>
                  <span>{formatBytes(userSubscription.transfer_enable - userSubscription.u - userSubscription.d)}</span>
                </div>
              )}
            </div>
          )}
          <p className="text-sm text-gray-600">
            请确认是否要继续创建新订单？
          </p>
        </div>
      </Modal> */}

    </div>
  );
};

export default OrderDetailPage;